<template>
  <div class="create-user-wrapper">
    <div class="create-user-container">
      <div class="form-header">
        <i class="pi pi-user-plus header-icon"></i>
        <h3 class="form-title">Создание нового пользователя</h3>
        <p class="form-subtitle">Заполните данные для регистрации пользователя в системе</p>
      </div>

      <Divider />

      <AbstractForm
          ref="form"
          :loading="formLoading"
          v-model="userForm"
          :on-commit="createUser"
          commit-label="Создать пользователя"
          commit-icon="pi pi-user-plus"
          class="user-form"
      />

      <Dialog
          v-model:visible="showPasswordDialog"
          header="Пользователь успешно создан"
          :modal="true"
          :closable="false"
          :closeOnEscape="false"
          class="password-dialog"
      >
        <div class="password-content">
          <div class="success-icon">
            <i class="pi pi-check-circle"></i>
          </div>

          <div class="user-info">
            <h4>{{ newUserData?.surname }} {{ newUserData?.name }} {{ newUserData?.patronymic || '' }}</h4>
            <p class="user-email">{{ newUserData?.email }}</p>
            <p class="user-role">{{ newUserData?.role === 'admin' ? '👑 Администратор' : '👤 Оператор' }}</p>
          </div>

          <Divider />

          <div class="password-box">
            <label>Временный пароль для входа:</label>
            <div class="password-value">
              <span class="password-text">{{ temporaryPassword }}</span>
              <Button
                  icon="pi pi-copy"
                  class="p-button-text p-button-rounded"
                  @click="copyPassword"
                  :tooltip="copied ? 'Скопировано!' : 'Скопировать пароль'"
                  tooltipOptions="{ position: 'top' }"
              />
            </div>
            <small class="password-hint">
              <i class="pi pi-exclamation-triangle"></i>
              Сохраните этот пароль. После первого входа пользователь сможет изменить его.
            </small>
          </div>
        </div>

        <template #footer>
          <Button
              label="Закрыть"
              icon="pi pi-times"
              class="p-button-text"
              @click="closeDialogAndReset"
          />
          <Button
              label="Скопировать и закрыть"
              icon="pi pi-copy"
              class="p-button-primary"
              @click="copyAndClose"
              autofocus
          />
        </template>
      </Dialog>

      <div class="form-footer" v-if="!formLoading">
        <small class="required-hint">
          <i class="pi pi-info-circle"></i>
          Поля с отметкой * обязательны для заполнения
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import AbstractForm from "@/components/AbstractForm.vue";
import FormModel from "@/model/FormModel";
import UserService from "@/service/UserService";
import ResponseService from "@/service/ResponseService";
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Divider from 'primevue/divider';

export default {
  name: "CreateUserForm",
  components: {
    AbstractForm,
    Dialog,
    Button,
    Divider
  },
  data() {
    return {
      formLoading: false,
      showPasswordDialog: false,
      temporaryPassword: '',
      newUserData: null,
      copied: false,
      userForm: {
        email: new FormModel('Email *', {
          type: 'text',
          required: true,
          placeholder: 'example@company.ru'
        }),
        surname: new FormModel('Фамилия *', {
          type: 'text',
          required: true,
          placeholder: 'Иванов'
        }),
        name: new FormModel('Имя *', {
          type: 'text',
          required: true,
          placeholder: 'Иван'
        }),
        patronymic: new FormModel('Отчество', {
          type: 'text',
          required: false,
          placeholder: 'Иванович'
        }),
        role: new FormModel('Роль *', {
          type: 'select',
          required: true,
          selectOptions: [
            { key: 'operator', value: '👤 Оператор' },
            { key: 'admin', value: '👑 Администратор' }
          ],
          selectOption: 'value',
          selectValue: 'key'
        })
      }
    }
  },
  methods: {
    createUser() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.userForm.email.value && !emailRegex.test(this.userForm.email.value)) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Некорректный email',
          detail: 'Введите корректный email адрес',
          life: 5000
        });
        return;
      }

      if (!this.userForm.email.value) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Некорректно заполнена форма',
          detail: 'Укажите email пользователя',
          life: 5000
        });
        return;
      }

      if (!this.userForm.surname.value) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Некорректно заполнена форма',
          detail: 'Укажите фамилию пользователя',
          life: 5000
        });
        return;
      }

      if (!this.userForm.name.value) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Некорректно заполнена форма',
          detail: 'Укажите имя пользователя',
          life: 5000
        });
        return;
      }

      if (!this.userForm.role.value) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Некорректно заполнена форма',
          detail: 'Выберите роль пользователя',
          life: 5000
        });
        return;
      }

      const userData = {
        email: this.userForm.email.value,
        surname: this.userForm.surname.value,
        name: this.userForm.name.value,
        patronymic: this.userForm.patronymic.value || null,
        role: this.userForm.role.value
      };

      this.formLoading = true;

      UserService.createUser(userData)
          .then((res) => {
            if (res.errors !== null) {
              this.$toast.add({
                severity: 'error',
                summary: res.message || 'Ошибка создания пользователя',
                detail: res.error,
                life: 5000
              });
            } else {
              this.temporaryPassword = res.temporaryPassword || res.password || 'Сгенерирован временный пароль';
              this.newUserData = userData;

              this.showPasswordDialog = true;

              this.$toast.add({
                severity: 'success',
                summary: 'Пользователь создан',
                detail: `${userData.surname} ${userData.name} успешно добавлен в систему`,
                life: 3000,
                icon: 'pi pi-check-circle'
              });
            }
          })
          .catch((err) => {
            ResponseService.handleErrorResponseWithForm(err, this.userForm, this.$toast);
          })
          .finally(() => {
            this.formLoading = false;
          });
    },

    copyPassword() {
      navigator.clipboard.writeText(this.temporaryPassword).then(() => {
        this.copied = true;
        this.$toast.add({
          severity: 'success',
          summary: 'Скопировано',
          detail: 'Пароль скопирован в буфер обмена',
          life: 2000,
          icon: 'pi pi-copy'
        });
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      }).catch(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось скопировать пароль',
          life: 3000
        });
      });
    },

    copyAndClose() {
      this.copyPassword();
      setTimeout(() => {
        this.closeDialogAndReset();
      }, 500);
    },

    closeDialogAndReset() {
      this.showPasswordDialog = false;
      this.temporaryPassword = '';
      this.newUserData = null;
      this.copied = false;
      this.clear();
    },

    clear() {
      this.$refs.form.clear();
    }
  }
}
</script>

<style scoped>
.create-user-wrapper {
  padding: 1rem;
}

.create-user-container {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.form-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.header-icon {
  font-size: 3rem;
  color: #3b82f6;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
}

.form-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0.5rem 0;
  color: #1e293b;
}

.form-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.form-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.required-hint {
  color: #94a3b8;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.required-hint i {
  font-size: 0.875rem;
}

:deep(.password-dialog) {
  width: 500px;
  max-width: 90vw;
}

:deep(.password-dialog .p-dialog-header) {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border-radius: 12px 12px 0 0;
}

:deep(.password-dialog .p-dialog-header .p-dialog-title) {
  color: white;
  font-weight: 600;
}

:deep(.password-dialog .p-dialog-content) {
  padding: 1.5rem;
}

.password-content {
  text-align: center;
}

.success-icon {
  font-size: 4rem;
  color: #10b981;
  margin-bottom: 1rem;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.user-info h4 {
  margin: 0.5rem 0;
  font-size: 1.2rem;
  color: #1e293b;
}

.user-email {
  margin: 0.25rem 0;
  color: #64748b;
  font-size: 0.875rem;
}

.user-role {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 500;
}

.password-box {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
}

.password-box label {
  display: block;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
}

.password-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #cbd5e1;
  margin-bottom: 0.75rem;
}

.password-text {
  font-family: 'Courier New', monospace;
  font-size: 1.1rem;
  font-weight: 600;
  color: #3b82f6;
  letter-spacing: 0.5px;
  word-break: break-all;
}

.password-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ef4444;
  font-size: 0.75rem;
  text-align: left;
}

.password-hint i {
  font-size: 0.875rem;
}

:deep(.user-form) {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

:deep(.p-inputtext),
:deep(.p-dropdown) {
  border-radius: 8px;
  transition: all 0.3s ease;
}

:deep(.p-inputtext:focus),
:deep(.p-dropdown:focus) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

:deep(.p-button) {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

:deep(.p-button:hover) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

:deep(.p-button:active) {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .create-user-container {
    padding: 1rem;
  }

  .form-title {
    font-size: 1.25rem;
  }

  .header-icon {
    font-size: 2.5rem;
  }

  .password-value {
    flex-direction: column;
    gap: 0.5rem;
  }

  .password-text {
    font-size: 0.9rem;
  }
}
</style>